import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PlusIcon, XIcon, BuildingIcon, UserIcon, FileTextIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { AddressAutocomplete } from '../components/AddressAutocomplete'
import { LeadManagementFields } from '../components/LeadManagementFields'
import { logActivity, ActivityTypes } from '../utils/activityLogger'
import { contactHasName, normalizeContactFields, serializeLeadProjects } from '../lib/leadHelpers'
import { BUILDING_TYPES } from '../components/property/types'

interface Contact {
  id: string
  first_name: string
  last_name: string
  name?: string
  title?: string
  phone: string
  extension?: string
  cell: string
  email: string
}

interface Project {
  id: string
  type: string
  address: string
  address_line_2?: string
  city: string
  state: string
  zip: string
  unit_count: string
  work_type: string
  notes: string
}

interface LeadSource {
  id: string
  name: string
}

interface UserProfile {
  id: string
  first_name?: string
  last_name?: string
  email: string
}

export default function LeadCreate() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [leadSources, setLeadSources] = useState<LeadSource[]>([])
  const [users, setUsers] = useState<UserProfile[]>([])
  const [serviceCategories, setServiceCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'company' | 'contacts' | 'projects' | 'notes'>(
    'company'
  )
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set(['1']))

  // Company Information
  // phone/email may be prefilled from an Inbox "new contact" link
  const [companyData, setCompanyData] = useState({
    name: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    phone: searchParams.get('phone') || '',
    email: searchParams.get('email') || '',
    website: '',
  })

  // Contact Information (array of contacts)
  const [contacts, setContacts] = useState<Contact[]>([
    {
      id: '1',
      first_name: '',
      last_name: '',
      title: '',
      phone: searchParams.get('phone') || '',
      extension: '',
      cell: '',
      email: searchParams.get('email') || '',
    },
  ])

  // Projects (array of projects)
  const [projects, setProjects] = useState<Project[]>([
    {
      id: '1',
      type: '',
      address: '',
      address_line_2: '',
      city: '',
      state: '',
      zip: '',
      unit_count: '',
      work_type: '',
      notes: '',
    },
  ])

  // Lead Management
  const [leadData, setLeadData] = useState({
    notes: '',
    lead_source_id: '',
    lead_source_other: '',
    assigned_to: '',
    next_activity_date: '',
  })

  // Lead Notes (time-stamped) - Now using lead_notes table
  const [leadNotes, setLeadNotes] = useState<
    Array<{ id: string; note: string; created_at: string; created_by_name?: string }>
  >([])
  const [newNote, setNewNote] = useState('')
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)
  const [customFields, setCustomFields] = useState<Record<string, any>>({})
  const [newCustomFieldKey, setNewCustomFieldKey] = useState('')
  const [newCustomFieldValue, setNewCustomFieldValue] = useState('')

  useEffect(() => {
    fetchLeadSources()
    fetchUsers()
    fetchServiceCategories()
  }, [])

  const fetchLeadSources = async () => {
    try {
      const { data, error } = await supabase
        .from('lead_sources')
        .select('id, name')
        .eq('is_active', true)
        .order('name')

      if (error) throw error
      setLeadSources(data || [])
    } catch (error) {
      console.error('Error fetching lead sources:', error)
    }
  }

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email')
        .order('first_name')

      if (error) throw error
      setUsers(data || [])
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }

  const fetchServiceCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .order('name')

      if (error) throw error
      setServiceCategories(data || [])
    } catch (error) {
      console.error('Error fetching service categories:', error)
    }
  }

  const addContact = () => {
    const newContact: Contact = {
      id: Date.now().toString(),
      first_name: '',
      last_name: '',
      phone: '',
      extension: '',
      cell: '',
      email: '',
    }
    setContacts([...contacts, newContact])
  }

  const removeContact = (id: string) => {
    setContacts(contacts.filter((c) => c.id !== id))
  }

  const updateContact = (id: string, field: keyof Contact, value: string) => {
    setContacts(contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)))
  }

  const addProject = () => {
    const newId = Date.now().toString()
    const newProject: Project = {
      id: newId,
      type: '',
      address: '',
      address_line_2: '',
      city: '',
      state: '',
      zip: '',
      unit_count: '',
      work_type: '',
      notes: '',
    }
    setProjects([...projects, newProject])
    setExpandedProjects((prev) => new Set([...Array.from(prev), newId]))
  }

  const removeProject = (id: string) => {
    setProjects(projects.filter((p) => p.id !== id))
  }

  const updateProject = (id: string, field: keyof Project, value: string) => {
    setProjects(projects.map((p) => (p.id === id ? { ...p, [field]: value } : p)))
  }

  const updateProjectAddress = (
    id: string,
    addressData: { address: string; city: string; state: string; zip: string }
  ) => {
    setProjects(
      projects.map((p) =>
        p.id === id
          ? {
              ...p,
              address: addressData.address,
              city: addressData.city,
              state: addressData.state,
              zip: addressData.zip,
            }
          : p
      )
    )
  }

  const addNote = () => {
    if (newNote.trim()) {
      // For new leads, use temporary ID until lead is created
      setLeadNotes([...leadNotes, {
        id: `temp_${Date.now()}`,
        note: newNote.trim(),
        created_at: new Date().toISOString(),
      }])
      setNewNote('')
    }
  }

  const removeNote = (noteId: string) => {
    setLeadNotes(leadNotes.filter((n) => n.id !== noteId))
  }

  // Validation and navigation
  const validateTab = (tab: 'company' | 'contacts' | 'projects' | 'notes'): boolean => {
    switch (tab) {
      case 'company':
        return !!companyData.name
      case 'contacts':
        return true // Contacts are optional
      case 'projects':
        return true // Projects are optional
      case 'notes':
        return true // Notes are optional
      default:
        return false
    }
  }

  const handleNext = () => {
    if (!validateTab(activeTab)) {
      setNotification({
        type: 'error',
        message: 'Please fill in all required fields before continuing',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    const tabs: Array<'company' | 'contacts' | 'projects' | 'notes'> = [
      'company',
      'contacts',
      'projects',
      'notes',
    ]
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1])
    }
  }

  const handleBack = () => {
    const tabs: Array<'company' | 'contacts' | 'projects' | 'notes'> = [
      'company',
      'contacts',
      'projects',
      'notes',
    ]
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1])
    }
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      await handleSubmit({ preventDefault: () => {} } as any)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Get primary contact if exists
      const primaryContact = contacts[0] || null
      const primaryNames = primaryContact ? normalizeContactFields(primaryContact) : null

      // Build insert object conditionally to avoid errors if column doesn't exist yet
      const insertData: any = {
        // Company Information
        company_name: companyData.name || null,
        company_address: companyData.address || null,
        company_city: companyData.city || null,
        company_state: companyData.state || null,
        company_zip: companyData.zip || null,
        company_phone: companyData.phone || null,
        company_email: companyData.email || null,
        company_website: companyData.website || null,
      }

      // Only include address_line_2 if it has a value (column may not exist in DB yet)
      if (companyData.address_line_2) {
        insertData.company_address_line_2 = companyData.address_line_2
      }

      const contactsData = contacts
        .filter((c) => contactHasName(c))
        .map((c) => {
          const names = normalizeContactFields(c)
          return { ...c, ...names }
        })

      const { data: newLead, error } = await supabase.from('leads').insert({
        ...insertData,

        // Contact Information (primary contact in legacy fields)
        contact_first_name: primaryNames?.first_name || '',
        contact_last_name: primaryNames?.last_name || '',
        phone: primaryContact?.phone || null,
        email: primaryContact?.email || null,

        // New Structured Data Fields
        contacts: contactsData.length > 0 ? contactsData : [],
        projects: serializeLeadProjects(projects),

        // Lead Management
        lead_source_id: leadData.lead_source_id || null,
        lead_source_other: leadData.lead_source_other?.trim() || null,
        assigned_to: leadData.assigned_to || null,
        next_activity_date: leadData.next_activity_date || null,
        stage: 'new' as const,
        priority: 'medium' as const,

        // Custom Fields
        custom_fields: Object.keys(customFields).length > 0 ? customFields : null,
      }).select().single()

      if (error) throw error

      // Insert notes into lead_notes table (if any)
      if (newLead?.id && leadNotes.length > 0) {
        const notesToInsert = leadNotes.map((note) => ({
          lead_id: newLead.id,
          note: typeof note === 'string' ? note : note.note || '',
          created_at: typeof note === 'object' && note.created_at ? note.created_at : new Date().toISOString(),
          created_by_name: typeof note === 'object' && note.created_by_name ? note.created_by_name : null,
        }))

        const { error: notesError } = await supabase
          .from('lead_notes')
          .insert(notesToInsert)

        if (notesError) {
          console.error('Error inserting lead notes:', notesError)
          // Don't fail the whole operation if notes fail
        }
      }

      // Log activity
      if (newLead?.id) {
        await logActivity({
          activity_type: ActivityTypes.LEAD_CREATED,
          entity_type: 'lead',
          entity_id: newLead.id,
          description: 'Lead created',
        })
      }

      setNotification({
        type: 'success',
        message: 'Lead created successfully!',
      })
      const conversationId = searchParams.get('conversationId')
      if (conversationId && newLead?.id) {
        try {
          await supabase
            .from('conversations')
            .update({ lead_id: newLead.id })
            .eq('id', conversationId)
        } catch {
          /* non-fatal */
        }
      }
      setTimeout(() => {
        if (conversationId) navigate(`/messages/${conversationId}?tab=external`)
        else navigate('/leads')
      }, 1500)
    } catch (error) {
      console.error('Error creating lead:', error)
      setNotification({
        type: 'error',
        message: 'Error creating lead: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  const projectTypes = BUILDING_TYPES

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
        <MobilePageHeader
          sticky
          title="Add New Lead"
          onBack={() => navigate('/leads')}
          backLabel="Back to Leads"
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tab Navigation */}
          <div className={mobile.surface}>
            <div className="sticky top-[3.25rem] z-10 flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
              <div
                className={`flex-1 min-w-[5.5rem] min-h-[48px] px-3 sm:px-4 flex items-center justify-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'company'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : activeTab === 'contacts' || activeTab === 'projects' || activeTab === 'notes'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <BuildingIcon className="w-4 h-4" />
                  Company
                </div>
              </div>
              <div
                className={`flex-1 min-w-[5.5rem] min-h-[48px] px-3 sm:px-4 flex items-center justify-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'contacts'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : activeTab === 'projects' || activeTab === 'notes'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <UserIcon className="w-4 h-4" />
                  Contacts
                </div>
              </div>
              <div
                className={`flex-1 min-w-[5.5rem] min-h-[48px] px-3 sm:px-4 flex items-center justify-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'projects'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : activeTab === 'notes'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <FileTextIcon className="w-4 h-4" />
                  Projects
                </div>
              </div>
              <div
                className={`flex-1 min-w-[7rem] min-h-[48px] px-3 sm:px-4 flex items-center justify-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'notes'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-400'
                }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <FileTextIcon className="w-4 h-4" />
                  Notes
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-4 sm:p-6">
              {activeTab === 'company' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <BuildingIcon className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={mobile.label}>
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={companyData.name}
                        onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
                        className={mobile.field}
                      />
                    </div>
                    <div>
                      <label className={mobile.label}>Phone</label>
                      <input
                        type="tel"
                        value={companyData.phone}
                        onChange={(e) => {
                          const formatted = e.target.value.replace(/\D/g, '').slice(0, 10)
                          const display =
                            formatted.length > 0
                              ? formatted.length <= 3
                                ? formatted
                                : formatted.length <= 6
                                ? `(${formatted.slice(0, 3)}) ${formatted.slice(3)}`
                                : `(${formatted.slice(0, 3)}) ${formatted.slice(
                                    3,
                                    6
                                  )}-${formatted.slice(6)}`
                              : ''
                          setCompanyData({ ...companyData, phone: display })
                        }}
                        placeholder="(123) 456-7890"
                        className={mobile.field}
                      />
                    </div>
                    <div>
                      <label className={mobile.label}>Email</label>
                      <input
                        type="email"
                        value={companyData.email}
                        onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                        className={mobile.field}
                      />
                    </div>
                    <div>
                      <label className={mobile.label}>
                        Website
                      </label>
                      <input
                        type="text"
                        value={companyData.website}
                        onChange={(e) =>
                          setCompanyData({ ...companyData, website: e.target.value })
                        }
                        className={mobile.field}
                        placeholder="www.example.com or example.com"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className={mobile.label}>
                        Address
                      </label>
                      <AddressAutocomplete
                        value={companyData.address}
                        onChange={(address) => setCompanyData({ ...companyData, address })}
                        onAddressSelect={(addressData) =>
                          setCompanyData({
                            ...companyData,
                            address: addressData.address,
                            city: addressData.city,
                            state: addressData.state,
                            zip: addressData.zip,
                          })
                        }
                        placeholder="Start typing address..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className={mobile.label}>
                        Address Line 2 <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={companyData.address_line_2}
                        onChange={(e) =>
                          setCompanyData({ ...companyData, address_line_2: e.target.value })
                        }
                        className={mobile.field}
                        placeholder="Office #, Suite, Unit, etc."
                      />
                    </div>
                    <div>
                      <label className={mobile.label}>City</label>
                      <input
                        type="text"
                        value={companyData.city}
                        onChange={(e) => setCompanyData({ ...companyData, city: e.target.value })}
                        className={mobile.field}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className={mobile.label}>State</label>
                      <input
                        type="text"
                        value={companyData.state}
                        onChange={(e) => setCompanyData({ ...companyData, state: e.target.value })}
                        className={mobile.field}
                        placeholder="NY"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <label className={mobile.label}>
                        Zip Code
                      </label>
                      <input
                        type="text"
                        value={companyData.zip}
                        onChange={(e) => setCompanyData({ ...companyData, zip: e.target.value })}
                        className={mobile.field}
                        placeholder="10001"
                      />
                    </div>
                  </div>

                  {/* Custom Fields Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Fields</h3>
                    <div className="space-y-3">
                      {Object.entries(customFields).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              {key}
                            </label>
                            <input
                              type="text"
                              value={value}
                              onChange={(e) =>
                                setCustomFields({ ...customFields, [key]: e.target.value })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newFields = { ...customFields }
                              delete newFields[key]
                              setCustomFields(newFields)
                            }}
                            className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove field"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex items-end gap-2 pt-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Field Name
                          </label>
                          <input
                            type="text"
                            value={newCustomFieldKey}
                            onChange={(e) => setNewCustomFieldKey(e.target.value)}
                            placeholder="e.g., Industry, Size, etc."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Value
                          </label>
                          <input
                            type="text"
                            value={newCustomFieldValue}
                            onChange={(e) => setNewCustomFieldValue(e.target.value)}
                            placeholder="Field value"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (newCustomFieldKey.trim() && newCustomFieldValue.trim()) {
                              setCustomFields({
                                ...customFields,
                                [newCustomFieldKey.trim()]: newCustomFieldValue.trim(),
                              })
                              setNewCustomFieldKey('')
                              setNewCustomFieldValue('')
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'contacts' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <UserIcon className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
                  </div>
                  <div className="space-y-4">
                    {contacts.map((contact, index) => (
                      <div key={contact.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium text-gray-900">
                            Contact {index + 1} {index === 0 && '(Primary)'}
                          </h3>
                          {contacts.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeContact(contact.id)}
                              className="p-1 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <XIcon className="w-5 h-5 text-red-600" />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={mobile.label}>
                              First Name {index === 0 ? '*' : ''}
                            </label>
                            <input
                              type="text"
                              required={index === 0}
                              value={contact.first_name}
                              onChange={(e) => updateContact(contact.id, 'first_name', e.target.value)}
                              className={mobile.field}
                            />
                          </div>
                          <div>
                            <label className={mobile.label}>
                              Last Name {index === 0 ? '*' : ''}
                            </label>
                            <input
                              type="text"
                              required={index === 0}
                              value={contact.last_name}
                              onChange={(e) => updateContact(contact.id, 'last_name', e.target.value)}
                              className={mobile.field}
                            />
                          </div>
                          <div>
                            <label className={mobile.label}>
                              Title
                            </label>
                            <input
                              type="text"
                              value={contact.title || ''}
                              onChange={(e) => updateContact(contact.id, 'title', e.target.value)}
                              placeholder="e.g., Manager, Director, etc."
                              className={mobile.field}
                            />
                          </div>
                          <div>
                            <label className={mobile.label}>
                              Email
                            </label>
                            <input
                              type="email"
                              value={contact.email}
                              onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                              className={mobile.field}
                            />
                          </div>
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <label className={mobile.label}>
                                Phone
                              </label>
                              <input
                                type="tel"
                                value={contact.phone}
                                onChange={(e) => {
                                  const formatted = e.target.value.replace(/\D/g, '').slice(0, 10)
                                  const display =
                                    formatted.length > 0
                                      ? formatted.length <= 3
                                        ? formatted
                                        : formatted.length <= 6
                                        ? `(${formatted.slice(0, 3)}) ${formatted.slice(3)}`
                                        : `(${formatted.slice(0, 3)}) ${formatted.slice(
                                            3,
                                            6
                                          )}-${formatted.slice(6)}`
                                      : ''
                                  updateContact(contact.id, 'phone', display)
                                }}
                                placeholder="(123) 456-7890"
                                className={mobile.field}
                              />
                            </div>
                            <div className="w-24">
                              <label className={mobile.label}>
                                Ext. <span className="text-gray-400 font-normal">(Optional)</span>
                              </label>
                              <input
                                type="text"
                                value={contact.extension || ''}
                                onChange={(e) =>
                                  updateContact(contact.id, 'extension', e.target.value)
                                }
                                placeholder="Ext"
                                maxLength={10}
                                className={mobile.field}
                              />
                            </div>
                          </div>
                          <div>
                            <label className={mobile.label}>
                              Cell
                            </label>
                            <input
                              type="tel"
                              value={contact.cell}
                              onChange={(e) => {
                                const formatted = e.target.value.replace(/\D/g, '').slice(0, 10)
                                const display =
                                  formatted.length > 0
                                    ? formatted.length <= 3
                                      ? formatted
                                      : formatted.length <= 6
                                      ? `(${formatted.slice(0, 3)}) ${formatted.slice(3)}`
                                      : `(${formatted.slice(0, 3)}) ${formatted.slice(
                                          3,
                                          6
                                        )}-${formatted.slice(6)}`
                                    : ''
                                updateContact(contact.id, 'cell', display)
                              }}
                              placeholder="(123) 456-7890"
                              className={mobile.field}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addContact}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    Add Contact
                  </button>
                </div>
              )}

              {activeTab === 'projects' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <FileTextIcon className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-semibold text-gray-900">Potential Projects</h2>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {projects.map((project, index) => (
                      <div key={project.id} className="border border-gray-200 rounded-lg">
                        <button
                          type="button"
                          onClick={() => {
                            const expanded = new Set(expandedProjects)
                            if (expanded.has(project.id)) {
                              expanded.delete(project.id)
                            } else {
                              expanded.add(project.id)
                            }
                            setExpandedProjects(expanded)
                          }}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`transform transition-transform ${
                                expandedProjects.has(project.id) ? 'rotate-180' : ''
                              }`}
                            >
                              ▼
                            </span>
                            <h3 className="text-lg font-medium text-gray-900">
                              Project {index + 1}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2">
                            {project.address && (
                              <span className="text-sm text-gray-600">{project.address}</span>
                            )}
                            {projects.length > 1 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  removeProject(project.id)
                                }}
                                className="p-1 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <XIcon className="w-5 h-5 text-red-600" />
                              </button>
                            )}
                          </div>
                        </button>
                        {expandedProjects.has(project.id) && (
                          <div className="border-t border-gray-200 p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                              <label className={mobile.label}>
                                Building Type
                              </label>
                                <select
                                  value={project.type}
                                  onChange={(e) =>
                                    updateProject(project.id, 'type', e.target.value)
                                  }
                                  className={mobile.field}
                                >
                                <option value="">Select type</option>
                                  {projectTypes.map((type) => (
                                    <option key={type} value={type}>
                                      {type}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className={mobile.label}>
                                  Unit Count
                                </label>
                                <input
                                  type="number"
                                  value={project.unit_count}
                                  onChange={(e) =>
                                    updateProject(project.id, 'unit_count', e.target.value)
                                  }
                                  className={mobile.field}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className={mobile.label}>
                                  Address
                                </label>
                                <AddressAutocomplete
                                  key={`project-address-${project.id}`}
                                  value={project.address}
                                  onChange={(address) =>
                                    updateProject(project.id, 'address', address)
                                  }
                                  onAddressSelect={(addressData) =>
                                    updateProjectAddress(project.id, addressData)
                                  }
                                  placeholder="Start typing project address..."
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className={mobile.label}>
                                  Apt/Suite #{' '}
                                  <span className="text-gray-400 font-normal">(Optional)</span>
                                </label>
                                <input
                                  type="text"
                                  value={project.address_line_2 || ''}
                                  onChange={(e) =>
                                    updateProject(project.id, 'address_line_2', e.target.value)
                                  }
                                  className={mobile.field}
                                  placeholder="Apartment, Suite, Unit, etc."
                                />
                              </div>
                              <div>
                                <label className={mobile.label}>
                                  City
                                </label>
                                <input
                                  type="text"
                                  value={project.city}
                                  onChange={(e) =>
                                    updateProject(project.id, 'city', e.target.value)
                                  }
                                  className={mobile.field}
                                  placeholder="City"
                                />
                              </div>
                              <div>
                                <label className={mobile.label}>
                                  State
                                </label>
                                <input
                                  type="text"
                                  value={project.state}
                                  onChange={(e) =>
                                    updateProject(project.id, 'state', e.target.value)
                                  }
                                  className={mobile.field}
                                  placeholder="NY"
                                  maxLength={2}
                                />
                              </div>
                              <div>
                                <label className={mobile.label}>
                                  Zip Code
                                </label>
                                <input
                                  type="text"
                                  value={project.zip}
                                  onChange={(e) => updateProject(project.id, 'zip', e.target.value)}
                                  className={mobile.field}
                                  placeholder="10001"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className={mobile.label}>
                                  Work Type
                                </label>
                                <select
                                  value={project.work_type}
                                  onChange={(e) =>
                                    updateProject(project.id, 'work_type', e.target.value)
                                  }
                                  className={mobile.field}
                                >
                                  <option value="">Select Work Type</option>
                                  {serviceCategories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                      {category.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <label className={mobile.label}>
                                  Project Notes
                                </label>
                                <textarea
                                  value={project.notes}
                                  onChange={(e) =>
                                    updateProject(project.id, 'notes', e.target.value)
                                  }
                                  rows={3}
                                  className={mobile.field}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addProject}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    Add Project
                  </button>
                  <p className="mt-3 text-sm text-gray-500">
                    Fill in at least an address or property type for each project. Projects are saved
                    when you click <strong>Create Lead</strong> on the Notes tab.
                  </p>
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Lead Management</h2>
                    <LeadManagementFields
                      leadSources={leadSources}
                      users={users}
                      value={leadData}
                      onChange={(patch) => setLeadData({ ...leadData, ...patch })}
                    />
                  </div>

                  {/* Lead Notes Section */}
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Lead Notes</h2>
                    <div className="flex gap-2 mb-4">
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            addNote()
                          }
                        }}
                        rows={3}
                        className={`${mobile.field} flex-1`}
                        placeholder="Add a note... (Ctrl+Enter to submit)"
                      />
                      <button
                        type="button"
                        onClick={addNote}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors self-start"
                      >
                        Add Note
                      </button>
                    </div>
                    <div className="space-y-3">
                      {leadNotes.length > 0 ? (
                        leadNotes
                          .slice()
                          .reverse()
                          .map((note) => (
                            <div
                              key={note.id}
                              className="flex gap-3 bg-gray-50 rounded-lg p-3"
                            >
                              <div className="flex-1">
                                <p className="text-sm text-gray-700">{note.note}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(note.created_at).toLocaleString()}
                                  {note.created_by_name && ` • ${note.created_by_name}`}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeNote(note.id)}
                                className="p-1 hover:bg-red-50 rounded transition-colors"
                              >
                                <XIcon className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          ))
                      ) : (
                        <p className="text-sm text-gray-500 italic">No notes yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:rounded-xl lg:border lg:border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/leads')}
              className={mobile.secondaryBtn}
            >
              Cancel
            </button>

            <div className="flex gap-2 sm:gap-3">
              {activeTab !== 'company' && (
                <button
                  type="button"
                  onClick={handleBack}
                  className={mobile.secondaryBtn}
                >
                  Back
                </button>
              )}
              {activeTab === 'notes' ? (
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={loading}
                  className={mobile.primaryBtn}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-4 h-4" />
                      <span className="sm:hidden">Create</span>
                      <span className="hidden sm:inline">Create Lead</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!validateTab(activeTab)}
                  className={mobile.primaryBtn}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </form>

      {/* Notification Popup Modal */}
      {notification && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div
            className={`relative z-10 bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-md w-full mx-0 sm:mx-4 shadow-xl ${
              notification.type === 'success'
                ? 'border-2 border-green-500'
                : 'border-2 border-red-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.type === 'success'
                    ? 'bg-green-100'
                    : 'bg-red-100'
                }`}
              >
                {notification.type === 'success' ? (
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-lg font-semibold ${
                    notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
